import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Map, Send } from "lucide-react";
import ServiceHeader from "@/components/ServiceHeader";
import Footer from "@/components/Footer";
import StickyCallButton from "@/components/StickyCallButton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { trackFormSubmit } from "@/lib/conversionTracking";
import AuMobileInput from "@/components/AuMobileInput";
import { AU_MOBILE_ERROR, isValidAuMobile, toE164AuMobile } from "@/lib/auPhone";

const TITLE = "Contact OZ WASTE CO. | General Enquiries Melbourne";
const DESCRIPTION =
  "Contact OZ WASTE CO. for general enquiries, existing bookings, partnerships, support or to check moving service availability across Melbourne.";
const URL = "https://ozwasteco.com.au/contact";
const EMAIL = "contact@ozmoveco.com.au";

const glass =
  "bg-white/70 backdrop-blur-xl border border-white/60 shadow-[0_8px_30px_rgba(26,26,26,0.06)]";

const enquiryTypes = [
  "General Enquiry",
  "Check Service Availability",
  "Existing Booking",
  "Partnership or Referral",
  "Business or Commercial Enquiry",
  "Feedback",
  "Other",
];

const upsertMeta = (kind: "name" | "property", key: string, content: string) => {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${kind}="${key}"]`);
  const created = !el;
  const prev = el?.getAttribute("content") ?? "";
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(kind, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
  return () => {
    if (created) el?.remove();
    else el?.setAttribute("content", prev);
  };
};

interface FormState {
  name: string;
  phone: string;
  email: string;
  enquiry_type: string;
  message: string;
}

const initialForm: FormState = {
  name: "",
  phone: "",
  email: "",
  enquiry_type: enquiryTypes[0],
  message: "",
};

const contactOptions = [
  { icon: Phone, label: "Phone", value: "1800 556 654", href: "tel:1800556654" },
  { icon: Mail, label: "Email", value: EMAIL, href: `mailto:${EMAIL}` },
  {
    icon: MapPin,
    label: "Business Address",
    value: (
      <>
        Warehouse Complex
        <br />
        34 King William Street
        <br />
        Broadmeadows VIC 3047
      </>
    ),
  },
  { icon: Map, label: "Service Area", value: "Melbourne, Victoria" },
];

const Contact = () => {
  const [form, setForm] = useState<FormState>(initialForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const cleanups = [
      upsertMeta("name", "description", DESCRIPTION),
      upsertMeta("property", "og:title", TITLE),
      upsertMeta("property", "og:description", DESCRIPTION),
      upsertMeta("property", "og:url", URL),
      upsertMeta("name", "twitter:title", TITLE),
      upsertMeta("name", "twitter:description", DESCRIPTION),
    ];
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical?.getAttribute("href") ?? "";
    canonical?.setAttribute("href", URL);

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = "ld-contact-page";
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      name: TITLE,
      description: DESCRIPTION,
      url: URL,
      mainEntity: {
        "@type": "MovingCompany",
        name: "OZ WASTE CO.",
        url: "https://ozwasteco.com.au/",
        email: EMAIL,
        telephone: "+61-1800-556-654",
        address: {
          "@type": "PostalAddress",
          streetAddress: "34 King William Street",
          addressLocality: "Broadmeadows",
          addressRegion: "VIC",
          postalCode: "3047",
          addressCountry: "AU",
        },
        areaServed: { "@type": "Place", name: "Melbourne, Victoria" },
        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+61-1800-556-654",
          email: EMAIL,
          contactType: "customer service",
          areaServed: "AU",
          availableLanguage: "English",
        },
      },
    });
    document.head.appendChild(ld);

    window.scrollTo(0, 0);

    return () => {
      document.title = prevTitle;
      cleanups.forEach((fn) => fn());
      if (canonical) canonical.setAttribute("href", prevCanonical);
      ld.remove();
    };
  }, []);

  const handleChange = (field: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const validate = () => {
    const e: Partial<Record<keyof FormState, string>> = {};
    const phone = form.phone.trim();
    const email = form.email.trim();

    if (!form.name.trim()) e.name = "Full name is required";
    if (!phone && !email) {
      e.phone = "Add a phone number or an email address so we can reply";
      e.email = "Add a phone number or an email address so we can reply";
    } else {
      if (phone && !isValidAuMobile(phone)) e.phone = AU_MOBILE_ERROR;
      if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) e.email = "Enter a valid email address";
    }
    if (!form.message.trim()) e.message = "Please tell us how we can help";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;

    setLoading(true);
    const phoneE164 = toE164AuMobile(form.phone);
    const { error } = await supabase.from("contact_messages").insert({
      name: form.name.trim(),
      phone: phoneE164 || null,
      email: form.email.trim() || null,
      enquiry_type: form.enquiry_type,
      message: form.message.trim(),
    });
    setLoading(false);

    if (error) {
      toast.error("Something went wrong. Please try again or call us on 1800 556 654.");
      return;
    }
    setForm(initialForm);
    void trackFormSubmit(
      "contact_form",
      { enquiry_type: form.enquiry_type },
      { email: form.email, phone: phoneE164 },
    );
    setSubmitted(true);
  };

  const inputClass = (field: keyof FormState) =>
    `w-full px-4 py-3.5 border-2 rounded-xl bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/30 focus:border-[#F64A01] focus:outline-none focus:shadow-[0_0_0_4px_rgba(246,74,1,0.18)] transition-colors ${
      errors[field] ? "border-red-400" : "border-[#1a1a1a]/10"
    }`;

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <ServiceHeader />

      <main className="flex-1">
        {/* Hero */}
        <section className="relative overflow-hidden bg-[#FDC00E]">
          <div
            className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(255,255,255,0.55),transparent_55%)]"
            aria-hidden="true"
          />
          <div className="container relative py-16 md:py-28">
            <div className="max-w-3xl">
              <h1 className="text-4xl md:text-6xl font-black text-[#1a1a1a] leading-[1.05] tracking-tight">
                Contact OZ WASTE CO.
              </h1>
              <p className="mt-6 text-lg md:text-2xl text-[#1a1a1a]/75 font-medium leading-relaxed">
                Have a question, need help, or want to check whether we service your area? Send our
                team a message and we'll get back to you as soon as possible.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <a
                  href="#contact-form"
                  className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:bg-orange transition-colors"
                >
                  <Send className="w-5 h-5" /> Send a Message
                </a>
                <a
                  href="tel:1800556654"
                data-call-source="contact_options"
                  aria-label="Call 1800 556 654"
                  className="inline-flex items-center justify-center gap-2 bg-white/60 backdrop-blur-xl border border-[#1a1a1a]/15 text-[#1a1a1a] px-8 py-4 text-base md:text-lg font-bold rounded-xl hover:bg-white/80 transition-colors"
                >
                  <Phone className="w-5 h-5" /> Call 1800 556 654
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Contact options */}
        <section className="py-20 md:py-24 bg-white">
          <div className="container">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-14 tracking-tight">
              How to Reach Us
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 max-w-6xl mx-auto">
              {contactOptions.map((c) => (
                <div key={c.label} className={`${glass} rounded-2xl p-6`}>
                  <div className="w-11 h-11 bg-[#FDC00E] rounded-full flex items-center justify-center mb-4">
                    <c.icon className="w-5 h-5 text-[#1a1a1a]" />
                  </div>
                  <p className="text-xs uppercase tracking-wider font-bold text-[#1a1a1a]/70 mb-1.5">
                    {c.label}
                  </p>
                  {c.href ? (
                    <a
                      href={c.href}
                      data-call-source="contact_reach_us_card"
                      className="text-[#1a1a1a] font-bold break-words hover:opacity-70 transition-opacity"
                    >
                      {c.value}
                    </a>
                  ) : (
                    <p className="text-[#1a1a1a] font-bold leading-snug">{c.value}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact form */}
        <section id="contact-form" className="py-20 md:py-24 bg-[#f7f7f7]">
          <div className="container max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] text-center mb-3 tracking-tight">
              Send Us a Message
            </h2>
            <p className="text-center text-[#1a1a1a]/75 mb-10">
              General enquiries, availability checks, existing bookings, partnerships and feedback.
            </p>

            {submitted ? (
              <div className="bg-[#FFF1E8] border-2 border-[#F64A01] rounded-2xl p-8 text-center">
                <h3 className="text-2xl font-bold text-[#1a1a1a] mb-2">Message received</h3>
                <p className="text-[#1a1a1a]/75 leading-relaxed">
                  Thanks for contacting OZ WASTE CO. Your message has been received and a member of
                  our team will be in touch shortly.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className={`${glass} rounded-2xl p-6 md:p-8 space-y-5`} noValidate>
                <div>
                  <label htmlFor="c-name" className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                    Full Name
                  </label>
                  <input
                    id="c-name"
                    type="text"
                    value={form.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    className={inputClass("name")}
                    placeholder="Your full name"
                  />
                  {errors.name && <p className="text-red-600 text-xs mt-1">{errors.name}</p>}
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="c-phone" className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                      Phone Number
                    </label>
                    <AuMobileInput
                      id="c-phone"
                      value={form.phone}
                      onChange={(v) => handleChange("phone", v)}
                      onBlur={() => {
                        if (form.phone.trim() && !isValidAuMobile(form.phone))
                          setErrors((prev) => ({ ...prev, phone: AU_MOBILE_ERROR }));
                      }}
                      hasError={Boolean(errors.phone)}
                      inputClassName="w-full px-4 py-3.5 bg-white text-[#1a1a1a] font-medium placeholder:text-[#1a1a1a]/30 focus:outline-none"
                    />
                    {errors.phone && <p className="text-red-600 text-xs mt-1">{errors.phone}</p>}
                  </div>
                  <div>
                    <label htmlFor="c-email" className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                      Email Address
                    </label>
                    <input
                      id="c-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => handleChange("email", e.target.value)}
                      className={inputClass("email")}
                      placeholder="you@example.com"
                    />
                    {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
                  </div>
                </div>

                <div>
                  <label htmlFor="c-type" className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                    Enquiry Type
                  </label>
                  <select
                    id="c-type"
                    value={form.enquiry_type}
                    onChange={(e) => handleChange("enquiry_type", e.target.value)}
                    className={inputClass("enquiry_type")}
                  >
                    {enquiryTypes.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="c-message" className="block text-sm font-bold text-[#1a1a1a] mb-1.5">
                    Message
                  </label>
                  <textarea
                    id="c-message"
                    rows={5}
                    value={form.message}
                    onChange={(e) => handleChange("message", e.target.value)}
                    className={`${inputClass("message")} resize-none`}
                    placeholder="How can we help?"
                  />
                  {errors.message && <p className="text-red-600 text-xs mt-1">{errors.message}</p>}
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#1a1a1a] text-white py-4 text-lg font-bold rounded-xl hover:bg-orange transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending…" : "Send Message"}
                </button>
                <p className="text-center text-xs text-[#1a1a1a]/70">
                  Chasing a price instead? Use the{" "}
                  <a href="/#quote" className="font-bold underline">
                    Get a Quote
                  </a>{" "}
                  form for moving pricing.
                </p>
              </form>
            )}
          </div>
        </section>

        {/* Helpful information */}
        <section className="py-20 md:py-24 bg-white">
          <div className="container max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-black text-[#1a1a1a] mb-8 tracking-tight text-center">
              Which Form Should I Use?
            </h2>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className={`${glass} rounded-2xl p-6`}>
                <h3 className="font-bold text-[#1a1a1a] text-lg mb-2">Need a moving price?</h3>
                <p className="text-[#1a1a1a]/75 text-sm leading-relaxed">
                  For an accurate moving price, use the Get a Quote page and give us your job
                  details.
                </p>
              </div>
              <div className={`${glass} rounded-2xl p-6`}>
                <h3 className="font-bold text-[#1a1a1a] text-lg mb-2">Have a general question?</h3>
                <p className="text-[#1a1a1a]/75 text-sm leading-relaxed">
                  For general questions, support, partnerships, existing bookings or checking
                  availability, use the Contact Us form.
                </p>
              </div>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/#quote"
                className="bg-[#1a1a1a] text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange transition-colors text-center"
              >
                Get a Quote
              </a>
              <a
                href="#contact-form"
                className="inline-flex items-center justify-center gap-2 border-2 border-[#1a1a1a] text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange/10 hover:border-orange hover:text-orange-deep transition-colors"
              >
                Contact Our Team
              </a>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 md:py-28 bg-[#FDC00E]">
          <div className="container max-w-3xl text-center">
            <h2 className="text-4xl md:text-5xl font-black text-[#1a1a1a] mb-5 tracking-tight">
              Need to speak with someone?
            </h2>
            <p className="text-lg md:text-xl text-[#1a1a1a]/75 mb-10 leading-relaxed">
              Call our team directly or send us a message and we'll help point you in the right
              direction.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:1800556654"
                data-call-source="contact_final_cta"
                aria-label="Call 1800 556 654"
                className="inline-flex items-center justify-center gap-2 bg-[#1a1a1a] text-white px-8 py-4 text-lg font-bold rounded-xl hover:bg-orange transition-colors"
              >
                <Phone className="w-5 h-5" /> Call 1800 556 654
              </a>
              <a
                href="#contact-form"
                className="inline-flex items-center justify-center gap-2 bg-white/60 backdrop-blur-xl border border-[#1a1a1a]/15 text-[#1a1a1a] px-8 py-4 text-lg font-bold rounded-xl hover:bg-white/80 transition-colors"
              >
                <Send className="w-5 h-5" /> Send a Message
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <StickyCallButton />
    </div>
  );
};

export default Contact;